import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type RecordingUploadStatus =
  | "QUEUED"
  | "QUEUED_OFFLINE"
  | "PENDING_EDGE_ACK"
  | "UPLOADING"
  | "RETRYING"
  | "STITCHING"
  | "UPLOADED"
  | "FAILED"
  | "CANCELLED"
  | "DOWNLOADING";

export interface RecordingUploadJob {
  jobId: string;
  recordingId: string;
  deviceId: string;
  batchId?: string;
  sliceRange?: string;
  status: RecordingUploadStatus;
  progress: number;
  startTime: number; // timestamp when added
  fileName?: string; // optional human-readable name
  reason?: string; // human-readable explanation (e.g. device offline)
  code?: string; // reason code, e.g. EVICTED_LOCALLY
  deviceName?: string;
  deviceOnline?: boolean;
  // Goes before the device's other uploads (an incident, or "Upload first").
  urgent?: boolean;
  // The requested window, to request it again.
  window?: { start: string; end: string; pipelines: string[] };
}

interface RecordingUploadState {
  activeUploads: Record<string, RecordingUploadJob>; // keyed by jobId
  completedRecently: string[]; // jobIds of recently completed uploads (for notifications)
  dismissed: string[]; // failed jobIds the user closed
}

export const DISMISSED_UPLOADS_KEY = "insaion.dismissedUploads";

const loadDismissed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_UPLOADS_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const initialState: RecordingUploadState = {
  activeUploads: {},
  completedRecently: [],
  dismissed: loadDismissed(),
};

const recordingUploadSlice = createSlice({
  name: "recordingUpload",
  initialState,
  reducers: {
    addUploadJob: (state, action: PayloadAction<RecordingUploadJob>) => {
      state.activeUploads[action.payload.jobId] = action.payload;
    },

    // Merges a live status into a job, adding it if it is new. Fields left
    // undefined keep their value.
    upsertUploadJob: (
      state,
      action: PayloadAction<
        Partial<RecordingUploadJob> & Pick<RecordingUploadJob, "jobId">
      >,
    ) => {
      const { jobId } = action.payload;
      const prev = state.activeUploads[jobId];
      // A report that arrives after the job finished or was closed.
      if (
        !prev &&
        (state.completedRecently.includes(jobId) ||
          state.dismissed.includes(jobId))
      )
        return;
      const update = Object.fromEntries(
        Object.entries(action.payload).filter(([, v]) => v !== undefined),
      );
      state.activeUploads[jobId] = {
        ...(prev ?? {
          jobId,
          recordingId: "",
          deviceId: "",
          status: "UPLOADING",
          progress: 0,
          startTime: Date.now(),
        }),
        ...update,
      };
    },

    // Replaces the jobs with the server's list, fetched at fetchedAt. An open
    // job the server no longer lists has finished; one added after the fetch
    // started is kept.
    syncUploadJobs: (
      state,
      action: PayloadAction<{ jobs: RecordingUploadJob[]; fetchedAt: number }>,
    ) => {
      const { jobs, fetchedAt } = action.payload;
      const listed = new Set(jobs.map((j) => j.jobId));
      for (const job of Object.values(state.activeUploads)) {
        if (listed.has(job.jobId)) continue;
        if (
          job.startTime >= fetchedAt ||
          ["DOWNLOADING", "FAILED", "CANCELLED"].includes(job.status)
        )
          continue;
        delete state.activeUploads[job.jobId];
        state.completedRecently.push(job.jobId);
      }
      state.dismissed = state.dismissed.filter((id) => listed.has(id));
      for (const job of jobs) {
        if (!state.dismissed.includes(job.jobId)) {
          state.activeUploads[job.jobId] = {
            ...state.activeUploads[job.jobId],
            ...job,
          };
        }
      }
    },

    dismissUploadJob: (state, action: PayloadAction<string>) => {
      delete state.activeUploads[action.payload];
      state.dismissed.push(action.payload);
    },

    removeUploadJob: (state, action: PayloadAction<string>) => {
      delete state.activeUploads[action.payload];
    },

    markCompleted: (state, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (state.activeUploads[jobId]) {
        state.completedRecently.push(jobId);
        delete state.activeUploads[jobId];
      }
    },

    clearCompletedNotifications: (state) => {
      state.completedRecently = [];
    },

    clearAllUploads: (state) => {
      state.activeUploads = {};
      state.completedRecently = [];
    },
  },
});

export const {
  addUploadJob,
  upsertUploadJob,
  syncUploadJobs,
  dismissUploadJob,
  removeUploadJob,
  markCompleted,
  clearCompletedNotifications,
  clearAllUploads,
} = recordingUploadSlice.actions;

export const recordingUploadReducer = recordingUploadSlice.reducer;
